// Where the organisation's policies say the same thing twice, and which single
// policy already answers several standards.
//
// The standards overlap heavily — access control is ISO 27001, SOC 2, Cyber
// Essentials and PCI DSS all at once — so the useful advice is almost never
// "write another policy". It is: keep one document, and let the others point at
// it. Rules and wording come from the backend (backend/src/lib/policyOverlap.ts)
// so the page, the generation prompt and the compliance check agree.

import { useState } from "react";
import { theme, card } from "../theme";
import { displayControlRef } from "../lib/controlRef";

export interface PolicyOverlapItem {
  primary: { templateId: string; title: string; adopted: boolean; generated: boolean };
  secondary: { templateId: string; title: string; adopted: boolean; generated: boolean };
  shared: Array<{ frameworkId: string; controlRef: string }>;
  frameworks: string[];
  containment: boolean;
  suggestion: string;
}

export interface PolicyStandardSpread {
  templateId: string;
  title: string;
  frameworks: Array<{ frameworkId: string; label: string; controlRefs: string[] }>;
  note: string;
}

export interface PolicyOverlapReport {
  rule: string;
  multiStandard: PolicyStandardSpread[];
  duplicates: PolicyOverlapItem[];
}

/** Chip for a card: this one document discharges several standards. */
export function StandardSpreadChip({ spread }: { spread: PolicyStandardSpread }) {
  return (
    <span
      title={spread.note}
      style={{
        fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 4, whiteSpace: "nowrap",
        color: theme.color.accentText, background: theme.color.accentBg, border: `1px solid ${theme.color.border}`,
      }}
    >
      Covers {spread.frameworks.length} standards
    </span>
  );
}

/** The overlaps that involve one policy, shown inside its card. */
export function PolicyOverlapHints({ templateId, report }: { templateId: string; report: PolicyOverlapReport | null }) {
  if (!report) return null;
  const mine = report.duplicates.filter((o) => o.primary.templateId === templateId || o.secondary.templateId === templateId);
  const spread = report.multiStandard.find((s) => s.templateId === templateId);
  if (mine.length === 0 && !spread) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 14 }}>
      {spread && (
        <div style={{ fontSize: 11, color: theme.color.subtle, lineHeight: 1.6 }}>
          <strong style={{ color: theme.color.muted }}>One policy, {spread.frameworks.length} standards:</strong>{" "}
          {spread.frameworks.map((f) => `${f.label} ${f.controlRefs.map(displayControlRef).join(", ")}`).join(" · ")}. You do not need a separate
          version per standard.
        </div>
      )}
      {mine.map((o, i) => (
        <div key={i} style={{ fontSize: 11, color: theme.color.subtle, lineHeight: 1.6 }}>
          <strong style={{ color: theme.color.muted }}>
            {o.primary.templateId === templateId ? "Owns shared rules:" : "Reference, don't repeat:"}
          </strong>{" "}
          {o.suggestion}
        </div>
      ))}
    </div>
  );
}

/** Page-level summary: every duplication worth acting on, newest advice first. */
export function PolicyOverlapPanel({ report }: { report: PolicyOverlapReport | null }) {
  const [open, setOpen] = useState(false);
  if (!report || (report.duplicates.length === 0 && report.multiStandard.length === 0)) return null;

  const held = report.duplicates.filter((o) => o.primary.generated || o.secondary.generated);
  const shown = held.length > 0 ? held : report.duplicates;

  return (
    <div style={{ ...card, marginBottom: 16, padding: "12px 14px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading }}>Shared coverage</span>
        {report.multiStandard.length > 0 && (
          <span style={{ fontSize: 12, color: theme.color.subtle }}>
            {report.multiStandard.length} {report.multiStandard.length === 1 ? "policy answers" : "policies answer"} more than one standard
          </span>
        )}
        {shown.length > 0 && (
          <button
            onClick={() => setOpen((v) => !v)}
            style={{
              marginLeft: "auto", padding: "5px 12px", borderRadius: 3, fontSize: 11, fontWeight: 600, cursor: "pointer",
              border: `1px solid ${theme.color.borderStrong}`, background: "transparent", color: theme.color.muted,
            }}
          >
            {open ? "Hide" : `${shown.length} duplication${shown.length === 1 ? "" : "s"} to review`}
          </button>
        )}
      </div>

      <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 6, lineHeight: 1.6 }}>{report.rule}</div>

      {open && (
        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
          {shown.map((o, i) => (
            <div
              key={i}
              style={{
                background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`,
                borderRadius: 3, padding: "9px 12px", fontSize: 12, color: theme.color.text, lineHeight: 1.6,
              }}
            >
              {o.suggestion}
              <div style={{ fontSize: 10, color: theme.color.faint, marginTop: 4 }}>
                {o.frameworks.join(" · ")}
                {o.primary.adopted ? ` · "${o.primary.title}" is adopted` : ""}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
